import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { isString } from "../app/utils";
import {
	hasPendingOnboarding,
	nullAccessibility,
	setAccessibility,
	setAdminSessionSynced,
	setDistrict,
	setSchool,
	setUser,
} from "../app/userSlice";
import { siteApiSlice } from "../app/siteApiSlice";
import Footer from "../features/footer/Footer";
import Header from "../features/header/Header";
import { useGetSelfMutation } from "../features/users/usersApiSlice";
import { useSyncAdminSession } from "../features/login/useSyncAdminSession";
import { applyAccessibility } from "../features/accessibility/accessibilityUtil";

export default function Private(props: any) {
	const user = useAppSelector((state) => state.user);
	const {
		isLoggedIn,
		userId: loggedInUserId,
		isTeachersWriterAccount,
		requiresAccountConfirmation,
		bypassTermsOfService,
		isAdminSessionSynced,
		role,
		school,
		district,
	} = user;
	const [userId] = useState(loggedInUserId);
	// Only true once GetSelf has answered. Everything the sync effect keys on is
	// persisted to local storage, so without this it would act on rehydrated values.
	const [selfLoaded, setSelfLoaded] = useState(false);
	const syncInFlight = useRef(false);

	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const location = useLocation();

	const [callGetSelf] = useGetSelfMutation();
	const syncAdminSession = useSyncAdminSession();

	const pendingOnboarding = hasPendingOnboarding(user);

	useEffect(() => {
		callGetSelf()
			.unwrap()
			.then((payload) => {
				dispatch(setUser({ ...payload }));
				dispatch(
					setAccessibility(
						payload.accessibility
							? { ...payload.accessibility }
							: { ...nullAccessibility }
					)
				);
				dispatch(setSchool(payload.school));
				dispatch(setDistrict(payload.district));
				applyAccessibility(payload.accessibility);
				// Batched with the dispatches above, so the sync effect's first eligible
				// run sees a consistent snapshot.
				setSelfLoaded(true);
			})
			.catch((error: any) => {
				const e = isString(error) ? error : JSON.stringify(error);
				console.log(`error calling GetSelf, error = ${e}`);
				// Open the gate so the admin routes fall back to today's behavior rather
				// than sitting on a placeholder forever. selfLoaded stays false, so the
				// sync effect won't act on rehydrated state. On a 401 the base query has
				// already reset the user state and the effect below routes to /login.
				dispatch(setAdminSessionSynced(true));
			});
	}, []);

	// Push the school/district GetSelf resolved into the server-side session, which
	// several endpoints read as their only source (e.g. the School field on the
	// create-user form comes from _userSession.School alone). This is reactive
	// rather than one-shot because it has to WAIT for the terms of service and any
	// forced password change: until those are done the backend's
	// RequireConfirmedAccount filter rejects the selection call. Accepting the terms
	// and saving the new password each refresh those flags in redux, which is what
	// re-triggers this - so neither of those screens needs to know about the sync.
	useEffect(() => {
		if (!selfLoaded) return;
		if (!isLoggedIn || role === null) return;
		if (pendingOnboarding) {
			// Entering the pending state un-latches, so leaving it always re-syncs.
			if (isAdminSessionSynced) dispatch(setAdminSessionSynced(false));
			return;
		}
		if (isAdminSessionSynced || syncInFlight.current) return;

		// The ref (not a cleanup) is what keeps StrictMode's mount/cleanup/mount in
		// dev from firing two selection calls.
		syncInFlight.current = true;
		syncAdminSession({ role, school, district }).finally(() => {
			syncInFlight.current = false;
		});
		// school/district are intentionally not dependencies: they can never trigger a
		// sync (the isAdminSessionSynced check above always wins by the time they
		// change), and including them would let a header picker switch re-fire this.
	}, [selfLoaded, isLoggedIn, role, pendingOnboarding, isAdminSessionSynced]);

	useEffect(() => {
		if (!isLoggedIn || !loggedInUserId || loggedInUserId !== userId) {
			dispatch(siteApiSlice.util.resetApiState());
			if (!isTeachersWriterAccount || !isLoggedIn) navigate("/login");
		}
	}, [isLoggedIn, loggedInUserId]);

	useEffect(() => {
		if (!pendingOnboarding) return;

		if (!bypassTermsOfService) {
			navigate("/termsOfService");
		} else if (requiresAccountConfirmation) {
			navigate("/myAccount");
		}
	}, [location.pathname]);

	return (
		<div className="flex flex-col min-h-screen bg-white">
			<Header />
			<main className="mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-8 py-8 w-screen">
				<Outlet />
			</main>
			<Footer />
		</div>
	);
}
